import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantTier } from '@/hooks/useTenantTier';
import {
  HardHat,
  Receipt,
  Building2,
  Map as MapIcon,
  BarChart3,
  Brain,
  ArrowUpRight,
  ShieldCheck,
  Fingerprint,
  Layers,
  GitBranch,
  Users,
  FileLock2,
  ScrollText,
  Scale,
  History,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuickAction {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  roles: string[];
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Painel Executivo',
    description: 'Desempenho e riscos da carteira',
    href: '/empreendimentos/portfolio',
    icon: BarChart3,
    roles: ['admin','coadmin','admin_cluster','gestor_execucao','gestor_planejamento','gestor_qualidade','gestor','observador'],
  },
  {
    label: 'Inteligência de Risco',
    description: 'Criticidade e prognósticos da carteira',
    href: '/empreendimentos/inteligencia',
    icon: Brain,
    roles: ['admin','coadmin','admin_cluster','gestor_execucao','gestor_planejamento','gestor_qualidade','gestor'],
  },
  {
    label: 'Portal da Execução',
    description: 'Registro e validação de serviços',
    href: '/empreendimentos/execucao',
    icon: HardHat,
    roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor'],
  },
  {
    label: 'Portal de Medições',
    description: 'Boletins, glosas e governança financeira',
    href: '/empreendimentos/medicoes',
    icon: Receipt,
    roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor'],
  },
  {
    label: 'Contratos',
    description: 'Carteira completa e aditivos',
    href: '/empreendimentos/contratos',
    icon: Building2,
    roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor'],
  },
  {
    label: 'Inteligência Geoespacial',
    description: 'Visão territorial dos ativos',
    href: '/empreendimentos/mapa',
    icon: MapIcon,
    roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor'],
  },
];

const PILLARS: { label: string; caption: string }[] = [
  { label: 'Previsibilidade', caption: 'Cenários auditáveis em cada decisão' },
  { label: 'Lastro', caption: 'Cada número rastreável à fonte' },
  { label: 'Governança', caption: 'Validações em camadas, sem zonas cinzentas' },
];

// Bloco 2 — Princípios de Governança do Sistema (linguagem de negócio).
const GOVERNANCE_PRINCIPLES: { label: string; icon: LucideIcon }[] = [
  { label: 'Versionamento contratual', icon: GitBranch },
  { label: 'Segregação de funções', icon: Users },
  { label: 'Rastreabilidade de decisões', icon: Fingerprint },
  { label: 'Consistência de indicadores', icon: Layers },
  { label: 'Reconciliação contínua de estado', icon: Scale },
  { label: 'Metodologia uniforme de cálculo', icon: ScrollText },
];

// Bloco 3 — O que sustenta a confiabilidade (linguagem de auditoria, sem termos de arquitetura).
const RELIABILITY_SIGNALS: { title: string; caption: string; icon: LucideIcon }[] = [
  {
    title: 'Registro imutável de decisões',
    caption: 'Aprovações, medições e validações permanecem íntegras após consolidação.',
    icon: FileLock2,
  },
  {
    title: 'Reconciliação contínua de estado',
    caption: 'Os números exibidos são derivados da base contratual vigente e revalidados de forma transversal.',
    icon: Scale,
  },
  {
    title: 'Validação dupla de eventos críticos',
    caption: 'Operações sensíveis exigem confirmação interna e externa antes de produzir efeito contábil.',
    icon: ShieldCheck,
  },
  {
    title: 'Histórico contratual preservado',
    caption: 'Baselines e aditivos mantêm contexto e versão, sem sobreposição silenciosa.',
    icon: History,
  },
];

// Bloco 4 — Sinais operacionais visíveis no sistema (evidências reais já presentes na UI).
const OPERATIONAL_SIGNALS: { title: string; caption: string }[] = [
  {
    title: 'Versionamento contratual ativo',
    caption: 'Baseline original e aditivos visíveis em cada contrato, com histórico preservado.',
  },
  {
    title: 'Gates de qualidade e planejamento',
    caption: 'Execuções percorrem etapas de validação distintas antes de gerar efeito financeiro.',
  },
  {
    title: 'Estados consolidados de medição',
    caption: 'Cada registro evolui por estados explícitos — Em curso, Consolidado, Devolvido — sem ambiguidade.',
  },
  {
    title: 'Painel fiduciário com exposição e capital em risco',
    caption: 'Carteira de financiamento exibe exposição real, capital protegido e capital em risco por contrato.',
  },
  {
    title: 'Indicadores derivados de fonte única',
    caption: 'KPIs operacionais e financeiros compartilham origem auditável, sem cálculos paralelos.',
  },
];

const EmpHome: React.FC = () => {
  const { role, user } = useAuth();
  const { checkModuleAccess } = useTenantTier();

  const firstName = React.useMemo(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    const candidate =
      (typeof meta.full_name === 'string' && meta.full_name) ||
      (typeof meta.name === 'string' && meta.name) ||
      (user?.email ? String(user.email).split('@')[0] : '');
    return String(candidate).trim().split(/\s+/)[0] || '';
  }, [user]);

  const actions = QUICK_ACTIONS.filter(a => {
    if (role && !a.roles.includes(role)) return false;
    const moduleId = a.href.replace('/empreendimentos/', '').split('/')[0] || 'dashboard';
    return checkModuleAccess(moduleId);
  });

  return (
    <div
      className="emp-home relative -m-4 sm:-m-5 md:-m-6 flex flex-col min-h-[calc(100vh-4rem)]"
      style={{
        backgroundImage:
          'radial-gradient(1200px 600px at 85% -10%, hsl(var(--primary) / 0.045), transparent 60%), radial-gradient(900px 500px at -10% 110%, hsl(var(--brand) / 0.035), transparent 55%)',
      }}
    >
      <div className="flex-1 flex flex-col px-6 sm:px-10 lg:px-14 py-8 sm:py-10 lg:py-12 max-w-6xl w-full mx-auto">
        {/* Hero + Bloco 1 (definição institucional) */}
        <header className="max-w-[720px]">
          <div className="flex items-center gap-2.5 mb-5 animate-fade-in">
            <span className="h-px w-8 bg-foreground/25" />
            <p
              className="text-[10.5px] sm:text-[11px] font-medium tracking-[0.34em] uppercase text-muted-foreground"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              NetUno · Previsibilidade com Lastro
            </p>
          </div>

          <p
            className="text-[14px] sm:text-[15px] font-medium text-foreground/70 animate-fade-in"
            style={{ fontFamily: 'Manrope, sans-serif', animationDelay: '60ms' }}
          >
            {firstName ? <>Bem-vindo, <span className="text-foreground/85">{firstName}</span>.</> : 'Bem-vindo.'}
          </p>

          <h1
            className="mt-2 text-[22px] sm:text-[26px] lg:text-[30px] font-semibold leading-[1.35] tracking-tight text-foreground animate-fade-in"
            style={{ fontFamily: 'Manrope, sans-serif', animationDelay: '100ms' }}
          >
            Plataforma de governança operacional e financeira com lastro contratual auditável.
          </h1>

          <p
            className="mt-3 text-[13.5px] sm:text-[14px] leading-relaxed text-muted-foreground animate-fade-in max-w-[640px]"
            style={{ fontFamily: 'Manrope, sans-serif', animationDelay: '140ms' }}
          >
            Uma única fonte de verdade para gestão, execução e risco — com disciplina institucional aplicada
            de forma transversal a contratos, medições e indicadores.
          </p>
        </header>

        {/* Bloco 2 — Princípios de Governança do Sistema */}
        <section
          className="mt-8 sm:mt-10 animate-fade-in"
          style={{ animationDelay: '180ms' }}
          aria-label="Princípios de governança do sistema"
        >
          <div className="flex items-center gap-3 mb-3">
            <h2
              className="text-[10.5px] sm:text-[11px] font-semibold tracking-[0.3em] uppercase text-muted-foreground"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Princípios de governança
            </h2>
            <span className="h-px flex-1 bg-border/70" />
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2.5">
            {GOVERNANCE_PRINCIPLES.map((p) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.label}
                  className="flex items-center gap-2 text-[12.5px] text-foreground/80"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  <Icon className="w-3.5 h-3.5 text-foreground/50 flex-shrink-0" strokeWidth={1.7} />
                  <span>{p.label}</span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Quick actions */}
        {actions.length > 0 && (
          <section className="mt-8 sm:mt-10">
            <div className="flex items-center gap-3 mb-4">
              <h2
                className="text-[10.5px] sm:text-[11px] font-semibold tracking-[0.3em] uppercase text-muted-foreground"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Acesso rápido
              </h2>
              <span className="h-px flex-1 bg-border/70" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {actions.slice(0, 6).map((action, idx) => {
                const Icon = action.icon;
                return (
                  <NavLink
                    key={action.href}
                    to={action.href}
                    className={cn(
                      'group relative flex flex-col gap-2.5 rounded-xl border border-border/70 bg-card/80 backdrop-blur-sm',
                      'p-4 transition-all duration-300 ease-out',
                      'hover:border-foreground/25 hover:bg-card hover:-translate-y-[1px]',
                      'hover:shadow-[0_10px_30px_-18px_hsl(var(--foreground)/0.25)]',
                      'animate-fade-in'
                    )}
                    style={{ animationDelay: `${220 + idx * 35}ms` }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-muted/70 flex items-center justify-center transition-colors group-hover:bg-foreground/[0.06]">
                        <Icon className="w-[17px] h-[17px] text-foreground/75" strokeWidth={1.5} />
                      </div>
                      <ArrowUpRight
                        className="w-4 h-4 text-muted-foreground/35 transition-all duration-300 group-hover:text-foreground/80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={1.6}
                      />
                    </div>
                    <div>
                      <h3
                        className="text-[13.5px] font-semibold text-foreground tracking-tight leading-snug"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {action.label}
                      </h3>
                      <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground line-clamp-2">
                        {action.description}
                      </p>
                    </div>
                  </NavLink>
                );
              })}
            </div>
          </section>
        )}

        {/* Blocos institucionais 3 e 4 — apresentados em duas colunas em telas largas
            para não competir com KPIs e evitar scroll excessivo. */}
        <section
          className="mt-10 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 animate-fade-in"
          style={{ animationDelay: '320ms' }}
        >
          {/* Bloco 3 — O que sustenta a confiabilidade do sistema */}
          <div
            className="rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm p-5"
            aria-label="O que sustenta a confiabilidade do sistema"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-3.5 w-3.5 text-foreground/55" strokeWidth={1.8} />
              <h3
                className="text-[10.5px] font-semibold tracking-[0.28em] uppercase text-muted-foreground"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Confiabilidade do sistema
              </h3>
            </div>
            <ul className="space-y-3">
              {RELIABILITY_SIGNALS.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} className="flex items-start gap-2.5">
                    <Icon
                      className="h-3.5 w-3.5 mt-0.5 text-foreground/55 flex-shrink-0"
                      strokeWidth={1.7}
                    />
                    <div className="min-w-0">
                      <p
                        className="text-[12.5px] font-medium text-foreground/85 leading-tight"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {s.title}
                      </p>
                      <p className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                        {s.caption}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Bloco 4 — Sinais operacionais visíveis no sistema */}
          <div
            className="rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm p-5"
            aria-label="Sinais operacionais visíveis no sistema"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-3.5 w-3.5 text-foreground/55" strokeWidth={1.8} />
              <h3
                className="text-[10.5px] font-semibold tracking-[0.28em] uppercase text-muted-foreground"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Sinais operacionais visíveis
              </h3>
            </div>
            <ul className="space-y-3">
              {OPERATIONAL_SIGNALS.map((s) => (
                <li key={s.title} className="flex items-start gap-2.5">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-foreground/40 flex-shrink-0" />
                  <div className="min-w-0">
                    <p
                      className="text-[12.5px] font-medium text-foreground/85 leading-tight"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {s.title}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                      {s.caption}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pillars footer */}
        <footer className="mt-auto pt-8 sm:pt-10">
          <div className="border-t border-border/60 pt-5 grid grid-cols-3 gap-4 sm:gap-8">
            {PILLARS.map((p, idx) => (
              <div
                key={p.label}
                className="animate-fade-in"
                style={{ animationDelay: `${400 + idx * 50}ms` }}
              >
                <p
                  className="text-[13px] sm:text-sm font-semibold text-foreground tracking-tight"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {p.label}
                </p>
                <p className="mt-0.5 text-[11.5px] sm:text-xs leading-snug text-muted-foreground">
                  {p.caption}
                </p>
              </div>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default EmpHome;
